/**
 * CountryTable · Module C 分游戏列视图（参考 Figma 3259-18986）
 *
 * 表头：国家 / 每个游戏一列（HoverSortCommonSimple 排序：Asc / Des / Default）
 * 行：
 *   - 国家列：flag + 国名 + hover 时显示 view-details-icon，点击触发外层详情面板
 *   - 数值列：RingRatioBubble + PercentageItem 展示数值 + 占比 + 环比箭头
 *
 * ⚠️ 详情面板放在外层（与 table 同级，绝对定位贴 table 右边），高度同 table；
 *    state 由父组件接管 → 这里通过 onOpenDetail 回调通知。
 */
import React, { useMemo } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import RegionCountryItem from '@op/components/Snippets/RegionCountryItem';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { DBO_V } from '@op/utils/format';
import { useGameColorContext } from '../../context/GameColorContext';
import useIntls from '@opm/hooks/useIntls';
import { ICountryRowItem } from '../../types/country';
import { TGameMap } from '../../hooks/useGameMap';
import { ICountrySortByState } from '../../hooks/useCountryCompare';
import GameSortHeader from './GameSortHeader';
import './index.scss';
import { chartAllColors } from '@op/constants';

export interface ICountryTablePageInfo {
  page: number;
  page_size: number;
}

export interface ICountryTableProps {
  list: ICountryRowItem[];
  gameMap: TGameMap;
  /** 列顺序（美国行锁定） */
  gameOrder: string[];
  sortBy: ICountrySortByState;
  setSortBy: (s: ICountrySortByState) => void;
  /** 当前打开详情的国家 country_code（受控） */
  openDetailCode: string | null;
  /** 打开/关闭详情回调 */
  onOpenDetail: (countryCode: string | null) => void;
  loading?: boolean;
  /** 时间区间（用于 RingRatioBubble 的 newTime / oldTime） */
  newTime?: [string, string];
  oldTime?: [string, string];
  /** 前端分页 state（由父级共享 stacked / columns 视图） */
  pageInfo?: ICountryTablePageInfo;
  setPageInfo?: (info: ICountryTablePageInfo) => void;
}

const ROW_HEIGHT = 60;
const HEADER_HEIGHT = 56;
const MIN_HEIGHT = 320;

interface IGameValueCellProps {
  value: number;
  oldValue?: number;
  rowTotal: number;
  color: string;
  newTime?: [string, string];
  oldTime?: [string, string];
}

/** 单元格：数值 + 占比 + 环比百分比箭头（PercentageItem） + 进度条；hover 显示 RingRatioBubble */
function GameValueCell({
  value,
  oldValue,
  rowTotal,
  color,
  newTime,
  oldTime,
}: IGameValueCellProps) {
  const percent = DBO_V.percentage(value, rowTotal);
  const qoq = DBO_V.qoq(value, oldValue ?? 0, 6);

  return (
    <RingRatioBubble
      newTime={newTime ?? []}
      oldTime={oldTime ?? []}
      newValue={value}
      oldValue={oldValue ?? 0}
      formateValue={(v) => DBO_V.reviews(Number(v))}
    >
      <span className="dbom-country-table__value-cell">
        <span className="dbom-country-table__value-row">
          <span className="dbom-country-table__value-text">
            {value > 0 ? DBO_V.reviews(value) : '-'}
          </span>
          <PercentageItem percentage={qoq} />
        </span>
        <span className="dbom-country-table__value-bar">
          <span
            className="dbom-country-table__value-bar-fill"
            style={{ width: `${percent}`, backgroundColor: color }}
          />
        </span>
      </span>
    </RingRatioBubble>
  );
}

const DEFAULT_PAGE_INFO: ICountryTablePageInfo = { page: 1, page_size: 10 };

export default function CountryTable({
  list,
  gameMap,
  gameOrder,
  sortBy,
  setSortBy,
  openDetailCode,
  onOpenDetail,
  loading,
  newTime,
  oldTime,
  pageInfo: pageInfoProp,
  setPageInfo: setPageInfoProp,
}: ICountryTableProps) {
  const { intl } = useIntls();

  const labels = useMemo(
    () => ({
      country: intl('Country'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl],
  );

  /** 从 Context 获取全局游戏颜色映射 */
  const { getGameColor } = useGameColorContext();

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        key: 'country',
        header: labels.country,
        width: 200,
        fixed: 'left' as const,
        render: (row: ICountryRowItem) => {
          const isOpen = openDetailCode === row.country_code;
          return (
            <span
              className={`dbom-country-table__country-cell ${
                isOpen ? 'dbom-country-table__country-cell--active' : ''
              }`}
            >
              <RegionCountryItem
                market={row.country_code}
                iconStyle={{ width: 22, height: 16 }}
              />
              <span
                className="dbom-country-table__detail-icon"
                role="button"
                aria-label="detail"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenDetail(isOpen ? null : row.country_code);
                }}
              >
                <SvgIcon type="view-details-icon" />
              </span>
            </span>
          );
        },
      },
      ...gameOrder.map((gameId) => {
        return {
          key: gameId,
          header: <GameSortHeader gameId={gameId} gameMap={gameMap} />,
          align: 'right' as const,
          width: 240,
          render: (row: ICountryRowItem, _i: string, recordIndex: number) => {
            const found = row.games.find((g) => g.game_id === gameId);
            const color = chartAllColors[recordIndex % chartAllColors.length];
            return (
              <GameValueCell
                value={found?.value ?? 0}
                oldValue={found?.old_value}
                rowTotal={row.total}
                color={color}
                newTime={newTime}
                oldTime={oldTime}
              />
            );
          },
        };
      }),
    ];
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gameOrder,
    gameMap,
    labels,
    openDetailCode,
    newTime,
    oldTime,
    getGameColor,
  ]);

  /** 前端分页：受控（父传）或本地兜底 */
  const [localPageInfo, setLocalPageInfo] =
    React.useState<ICountryTablePageInfo>(DEFAULT_PAGE_INFO);
  const pageInfo = pageInfoProp || localPageInfo;
  const setPageInfo = setPageInfoProp || setLocalPageInfo;

  const pagedList = useMemo(() => {
    const start = (pageInfo.page - 1) * pageInfo.page_size;
    return list.slice(start, start + pageInfo.page_size);
  }, [list, pageInfo]);

  const tableHeight = useMemo(
    () => Math.max(MIN_HEIGHT, HEADER_HEIGHT + pagedList.length * ROW_HEIGHT),
    [pagedList.length],
  );

  const recordKey = useMemo(() => (r: ICountryRowItem) => r.country_code, []);

  return (
    <TableCommonHook
      className="dbom-country-table"
      records={pagedList}
      columns={columns}
      recordKey={recordKey}
      total={list.length}
      pageInfo={pageInfo}
      setPageInfo={setPageInfo}
      defaultPageInfo={DEFAULT_PAGE_INFO}
      height={tableHeight}
      loading={loading}
      defaultSort={sortBy}
      sortClearable
      sortColumns={gameOrder}
      changeSort={(item) => {
        setSortBy(item);
      }}
      bordered={'all'}
    />
  );
}
